/**
 * Bringing her chart notes over from Boulevard.
 *
 * "There is about 1,000 clients and some I really don't need… the most
 *  important thing to get out of blvd is chart notes and lab work."
 *
 * Boulevard will hand over whatever shape it feels like, so this does not
 * assume one: pick the file, say which column is which, and it tells you what
 * it found before anything is written. Notes land signed, in the name of
 * whoever wrote them, dated when they were written.
 */
import { useState } from "react";
import { Upload, FileText, Check, AlertTriangle } from "lucide-react";
import { Button, Modal, ModalFooter, Select, Label, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { parseCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

/** What a column can be. Only the note itself and a way to find the client are needed. */
const TARGETS: { key: string; label: string; hint?: string }[] = [
  { key: "", label: "— ignore this column —" },
  { key: "client_email", label: "Client email", hint: "best way to match" },
  { key: "client_phone", label: "Client mobile" },
  { key: "client_name", label: "Client name", hint: "first and last together" },
  { key: "date", label: "Date written" },
  { key: "author", label: "Written by" },
  { key: "title", label: "Note title" },
  { key: "body", label: "The note itself", hint: "required" },
  // "can we seperate BLVD the earlier notes from blvd into Labs, charting
  //  notes and appointments"
  { key: "kind", label: "Lab / note / appointment", hint: "sends each row to the right place" },
];

const guess = (header: string): string => {
  const h = header.toLowerCase().replace(/[^a-z]/g, "");
  if (/email/.test(h)) return "client_email";
  if (/phone|mobile|cell/.test(h)) return "client_phone";
  if (/clientname|patientname|^name$|fullname/.test(h)) return "client_name";
  if (/date|when|created/.test(h)) return "date";
  if (/author|provider|staff|signedby|writtenby/.test(h)) return "author";
  // "type" is the column that usually says lab / note / appointment, so it
  // guesses the split rather than the title.
  if (/^(type|kind|category|record ?type)$/.test(h)) return "kind";
  if (/title|subject|type|service/.test(h)) return "title";
  if (/note|chart|body|text|content/.test(h)) return "body";
  return "";
};

interface Result {
  imported: number; already_there: number; no_client: number; empty: number; unmatched: string[];
  /** How the rows split across the three places they can land. */
  filed?: { note: number; lab: number; appointment: number };
}

export function NotesImportDialog({
  open, onClose, onImported,
}: { open: boolean; onClose: () => void; onImported: () => void }) {
  const toast = useToast();
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  /**
   * "can we seperate BLVD the earlier notes from blvd into Labs, charting
   *  notes and appointments"
   *
   * Where a row goes when the file has no column saying so - one export of
   * labs, say. With a "Lab / note / appointment" column mapped, each row
   * decides for itself and this is only the fallback.
   */
  const [defaultKind, setDefaultKind] = useState<"note" | "lab" | "appointment">("note");
  const [result, setResult] = useState<Result | null>(null);
  /**
   * "the area in blvd can you open it up to paste please asap much easier then
   *  download upload"
   *
   * Boulevard is making her go chart by chart, so asking her to export a file
   * first is a step too many. She can select the rows, copy, and drop them
   * straight in here - which arrives tab-separated and is read the same way.
   */
  const [how, setHow] = useState<"paste" | "file">("paste");
  const [pasted, setPasted] = useState("");

  const reset = () => {
    setHeaders([]); setRows([]); setMapping({}); setFileName(""); setResult(null); setPasted("");
  };
  const close = () => { reset(); onClose(); };

  const load = (text: string, name: string) => {
    const { headers: h, rows: r } = parseCsv(text);
    setHeaders(h);
    setRows(r);
    setFileName(name);
    setResult(null);
    setMapping(Object.fromEntries(h.map((x) => [x, guess(x)])));
  };

  const pick = async (file: File) => load(await file.text(), file.name);

  const readPaste = (text: string) => {
    setPasted(text);
    if (text.trim().length === 0) { setHeaders([]); setRows([]); return; }
    load(text, "pasted");
  };

  const mapped = new Set(Object.values(mapping).filter(Boolean));
  const canFindClient = mapped.has("client_email") || mapped.has("client_phone") || mapped.has("client_name");
  const ready = rows.length > 0 && mapped.has("body") && canFindClient;

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post<Result>("/charts/import", {
        rows, mapping, source: "boulevard",
        // Where a row goes when the file does not say, row by row.
        default_kind: defaultKind,
      });
      setResult(r);
      onImported();
      toast.success(`${r.imported} note${r.imported === 1 ? "" : "s"} brought over`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "That import failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={close} title="Bring chart notes over from Boulevard" size="lg">
      {result ? (
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-sm text-emerald-300">
            <Check className="h-4 w-4" />
            {result.imported} record{result.imported === 1 ? "" : "s"} brought over
            {result.filed && (result.filed.lab > 0 || result.filed.appointment > 0)
              ? ` - ${result.filed.note} chart note${result.filed.note === 1 ? "" : "s"}, `
                + `${result.filed.lab} lab${result.filed.lab === 1 ? "" : "s"}, `
                + `${result.filed.appointment} past visit${result.filed.appointment === 1 ? "" : "s"}.`
              : " on the clients' charts."}
          </p>
          {result.already_there > 0 && (
            <p className="text-xs text-zinc-400">
              {result.already_there} were already here from a previous run, so they were left alone.
            </p>
          )}
          {result.no_client > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
              <p className="flex items-center gap-2 text-sm text-amber-200">
                <AlertTriangle className="h-4 w-4" />
                {result.no_client} could not be matched to a client
              </p>
              <p className="mt-1 text-xs leading-relaxed text-zinc-300">
                Nothing was guessed - a note on the wrong chart is worse than one not brought over.
                Usually it means that client is not in the system yet, or their email differs from
                Boulevard's. Import the client list first, then run this again.
              </p>
              {result.unmatched.length > 0 && (
                <p className="mt-1.5 text-[11px] text-zinc-500">{result.unmatched.join(" · ")}</p>
              )}
            </div>
          )}
          {result.empty > 0 && (
            <p className="text-xs text-zinc-500">{result.empty} rows had no note in them.</p>
          )}
          <ModalFooter>
            <Button variant="ghost" onClick={reset}>Import another file</Button>
            <Button onClick={close}>Done</Button>
          </ModalFooter>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs leading-relaxed text-zinc-400">
            One note per row, any columns - you say which is which below. Copy the rows straight
            out of Boulevard and paste them, or use a file if they give you one. Notes arrive
            signed, in the name of whoever wrote them, on the date they were written, and are
            marked as coming from Boulevard.
          </p>

          <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-inset ring-zinc-700">
            {([["paste", "Paste it in"], ["file", "Use a file"]] as const).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => { setHow(k); reset(); }}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium",
                  how === k ? "bg-indigo-500/15 text-indigo-200" : "text-zinc-400 hover:text-zinc-200",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {how === "paste" ? (
            <div>
              <textarea
                value={pasted}
                onChange={(e) => readPaste(e.target.value)}
                rows={8}
                placeholder={"Select the rows in Boulevard, copy them, and paste here.\nInclude the header row if there is one."}
                className="w-full rounded-xl border border-zinc-700 bg-zinc-900/60 px-3 py-2 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
              />
              {rows.length > 0 && (
                <p className="mt-1 text-xs text-emerald-300">
                  Read {rows.length} {rows.length === 1 ? "row" : "rows"} and {headers.length} columns.
                </p>
              )}
            </div>
          ) : (
            <label className={cn(
              "flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-6 text-sm",
              fileName ? "border-zinc-700 text-zinc-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-600",
            )}>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && void pick(e.target.files[0])}
              />
              {fileName ? <FileText className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
              {fileName ? `${fileName} — ${rows.length} rows` : "Choose the file Boulevard gave you"}
            </label>
          )}

          {headers.length > 0 && (
            <div className="space-y-2">
              <Label>Which column is which</Label>
              <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-lg border border-zinc-800 p-2">
                {headers.map((h) => (
                  <div key={h} className="flex items-center gap-2">
                    <span className="w-40 shrink-0 truncate text-xs text-zinc-400" title={h}>{h}</span>
                    <Select
                      value={mapping[h] ?? ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                      className="h-8 flex-1 text-xs"
                    >
                      {TARGETS.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}{t.hint ? ` (${t.hint})` : ""}
                        </option>
                      ))}
                    </Select>
                    <span className="hidden w-48 shrink-0 truncate text-[11px] text-zinc-600 sm:block">
                      {rows[0]?.[h]?.slice(0, 40)}
                    </span>
                  </div>
                ))}
              </div>
              <div>
                <Label htmlFor="default-kind">
                  {mapped.has("kind") ? "When that column is blank, treat the row as" : "This whole file is"}
                </Label>
                <Select
                  id="default-kind"
                  value={defaultKind}
                  onChange={(e) => setDefaultKind(e.target.value as "note" | "lab" | "appointment")}
                  className="h-8 text-xs"
                >
                  <option value="note">Chart notes</option>
                  <option value="lab">Labs - filed under Lab results on the client</option>
                  <option value="appointment">Past visits - history, nobody is told</option>
                </Select>
                <p className="mt-1 text-[11px] text-zinc-500">
                  {mapped.has("kind")
                    ? "Rows that say lab, visit or note go where they belong on their own."
                    : "Map a \u201cLab / note / appointment\u201d column above to split one file three ways."}
                </p>
              </div>
              {!mapped.has("body") && (
                <p className="text-xs text-amber-300">Say which column holds the note itself.</p>
              )}
              {!canFindClient && (
                <p className="text-xs text-amber-300">
                  Say which column identifies the client - their email, mobile or name.
                </p>
              )}
            </div>
          )}

          <ModalFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button loading={busy} disabled={!ready} onClick={() => void run()}>
              Bring {rows.length > 0 ? `${rows.length} ` : ""}notes over
            </Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}
